/**
 * Button Component Library
 * Standardized button component with consistent styling across the application
 * Based on existing button patterns in the Chaggerheart codebase
 */

import React from 'react';
import { cn } from '../../lib/utils';

/**
 * Button variants following the application's color scheme
 * Each variant represents a different semantic meaning or campaign frame action
 */
const variants = {
  // Primary variant - default app style with red hover
  primary: 'bg-gray-800 text-gray-100 hover:text-red-400 hover:bg-red-900/30 border border-gray-600 hover:border-red-700',

  // Danger variant - destructive actions
  danger: 'bg-red-600 text-white hover:bg-red-700 border border-red-700 hover:border-red-800',

  // Danger outline - less prominent destructive actions
  dangerOutline: 'bg-red-900 text-white hover:bg-red-800 border border-red-700',

  // Campaign action variants
  loot: 'bg-amber-800 text-amber-200 hover:bg-amber-700 hover:text-white border border-amber-600',
  harvest: 'bg-green-800 text-green-200 hover:bg-green-700 hover:text-white border border-green-600',
  feast: 'bg-orange-800 text-orange-200 hover:bg-orange-700 hover:text-white border border-orange-600',
  magic: 'bg-purple-800 text-purple-200 hover:bg-purple-700 hover:text-white border border-purple-600',
  downtime: 'bg-blue-800 text-blue-200 hover:bg-blue-700 hover:text-white border border-blue-600',

  // Utility variants
  secondary: 'bg-gray-700 text-gray-200 hover:bg-gray-600 border border-gray-600',
  ghost: 'bg-transparent text-gray-300 hover:bg-gray-800 border border-transparent hover:border-gray-600',
  outline: 'bg-transparent text-gray-100 hover:bg-gray-800 border border-gray-600',
};

/**
 * Size variants for different use cases
 */
const sizes = {
  sm: 'px-2 py-1 text-sm',
  md: 'px-4 py-2 text-base',
  lg: 'px-6 py-3 text-base',
  xl: 'px-8 py-4 text-lg',
};

/**
 * Button component
 *
 * @param {Object} props - Component props
 * @param {string} [props.variant='primary'] - Button style variant
 * @param {string} [props.size='md'] - Button size
 * @param {boolean} [props.fullWidth=false] - Whether button should take full width
 * @param {boolean} [props.disabled=false] - Whether button is disabled
 * @param {string} [props.className] - Additional CSS classes
 * @param {React.ReactNode} props.children - Button content
 * @param {Function} [props.onClick] - Click handler
 * @param {string} [props.type='button'] - Button type attribute
 * @param {string} [props.ariaLabel] - Accessibility label
 *
 * @example
 * <Button variant="primary" onClick={handleClick}>
 *   Create Campaign
 * </Button>
 *
 * @example
 * <Button variant="loot" fullWidth>
 *   🏺 Discover Loot
 * </Button>
 *
 * @example
 * <Button variant="danger" size="sm" disabled>
 *   Delete Character
 * </Button>
 */
export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  disabled = false,
  className,
  children,
  onClick,
  type = 'button',
  ariaLabel,
  ...props
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn(
        // Base styles
        'rounded-md font-medium transition-all duration-200',
        'focus:outline-none focus:ring-2 focus:ring-red-700 focus:ring-offset-2 focus:ring-offset-gray-900',

        // Variant styles
        variants[variant],

        // Size styles
        sizes[size],

        // Full width
        fullWidth && 'w-full',

        // Disabled styles
        disabled && 'opacity-50 cursor-not-allowed',

        // Custom classes
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}

/**
 * Icon Button - for buttons with only an icon
 * Ensures proper touch target size and accessibility
 */
export function IconButton({
  variant = 'ghost',
  size = 'md',
  disabled = false,
  className,
  children,
  onClick,
  ariaLabel,
  ...props
}) {
  const iconSizes = {
    sm: 'min-h-[36px] min-w-[36px] p-2',
    md: 'min-h-[44px] min-w-[44px] p-2',
    lg: 'min-h-[48px] min-w-[48px] p-3',
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn(
        // Base styles
        'rounded-full inline-flex items-center justify-center',
        'transition-all duration-200',
        'focus:outline-none focus:ring-2 focus:ring-red-700',

        // Variant styles
        variants[variant],

        // Size with touch targets
        iconSizes[size],

        // Disabled styles
        disabled && 'opacity-50 cursor-not-allowed',

        // Custom classes
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}

/**
 * Button Group - for grouping related buttons
 */
export function ButtonGroup({ children, className }) {
  return (
    <div className={cn('flex gap-2', className)}>
      {children}
    </div>
  );
}

export default Button;
